import type { MarkdownDocument } from '@markbird/shared';
import { db, type DocLink } from './db';
import { documentKeys, key, parseWikiRefs } from './wikilinks';

/**
 * The wikilink index: which document links to which, kept current on write.
 *
 * The naive alternative — scan every document's text whenever backlinks are
 * needed — is O(library) per render, and was being driven by a live query that
 * re-fires on each autosave. This trades that for O(document) on write and an
 * indexed lookup on read, which is what makes backlinks, unresolved links,
 * orphans and the graph affordable at vault size.
 *
 * The table is *derived*: every row can be reconstructed from document content,
 * so a schema change or a suspected inconsistency is repaired by rebuilding,
 * never by migrating. `REBUILD_TOKEN` is the lever — bump it whenever the
 * extraction rules change, and the whole index is rebuilt on next start.
 */

const VERSION_KEY = 'linkIndexVersion';
const REBUILD_TOKEN = 1;

/** Extract the rows a document contributes to the index. */
export function linkRowsFor(id: string, content: string): DocLink[] {
  const seen = new Set<string>();
  const rows: DocLink[] = [];
  for (const ref of parseWikiRefs(content)) {
    // A same-document `[[#Heading]]` is navigation, not a link between notes.
    if (!ref.target) continue;
    // One row per distinct destination: ten links to the same note are one edge.
    const dedupe = `${ref.target} ${ref.heading ?? ''} ${ref.block ?? ''} ${ref.embed}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    rows.push({
      from: id,
      toKey: ref.target,
      rawTarget: ref.rawTarget,
      heading: ref.heading,
      block: ref.block,
      embed: ref.embed,
    });
  }
  return rows;
}

/** Re-extract one document's outgoing links. Call after any content write. */
export async function indexDocumentLinks(id: string, content: string): Promise<void> {
  const rows = linkRowsFor(id, content);
  await db.transaction('rw', db.links, async () => {
    await db.links.where('from').equals(id).delete();
    if (rows.length) await db.links.bulkAdd(rows);
  });
}

/** Drop a document's outgoing links (on permanent delete). */
export async function removeDocumentLinks(id: string): Promise<void> {
  await db.links.where('from').equals(id).delete();
}

/** Rebuild the whole index from document content. Safe to call at any time. */
export async function rebuildLinkIndex(): Promise<void> {
  const docs = await db.documents.toArray();
  const rows = docs.flatMap((d) => linkRowsFor(d.id, d.content));
  await db.transaction('rw', db.links, async () => {
    await db.links.clear();
    if (rows.length) await db.links.bulkAdd(rows);
  });
}

/** Build the index on first run, and after the extraction rules change. */
export async function ensureLinkIndex(): Promise<void> {
  const row = await db.kv.get(VERSION_KEY);
  if (row?.value === REBUILD_TOKEN) return;
  await rebuildLinkIndex();
  await db.kv.put({ key: VERSION_KEY, value: REBUILD_TOKEN });
}

/** Ids of documents linking to any of `keys` (a document's title and aliases). */
export async function backlinkIds(keys: string[], excludeId?: string): Promise<string[]> {
  if (keys.length === 0) return [];
  const rows = await db.links.where('toKey').anyOf(keys).toArray();
  const ids = new Set<string>();
  for (const r of rows) {
    if (r.from !== excludeId) ids.add(r.from);
  }
  return [...ids];
}

/** Documents linking to `doc`, resolved and ordered newest-first. */
export async function backlinksFor(
  doc: Pick<MarkdownDocument, 'id' | 'title' | 'aliases'>,
): Promise<MarkdownDocument[]> {
  const ids = await backlinkIds(documentKeys(doc), doc.id);
  if (ids.length === 0) return [];
  const docs = await db.documents.bulkGet(ids);
  return docs
    .filter((d): d is MarkdownDocument => !!d && d.deletedAt == null)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** A document's outgoing links, in source order. */
export function outgoingLinks(id: string): Promise<DocLink[]> {
  return db.links.where('from').equals(id).toArray();
}

/** A link target that matches no document, plus the documents that want it. */
export interface UnresolvedLink {
  /** Target as written, so the missing note can be created with its real name. */
  rawTarget: string;
  toKey: string;
  from: string[];
}

/** Every link target that matches no document title or alias. */
export async function unresolvedLinks(): Promise<UnresolvedLink[]> {
  const [rows, docs] = await Promise.all([db.links.toArray(), db.documents.toArray()]);
  const known = new Set<string>();
  for (const d of docs) {
    if (d.deletedAt != null) continue;
    for (const k of documentKeys(d)) known.add(k);
  }
  const live = new Set(docs.filter((d) => d.deletedAt == null).map((d) => d.id));

  const byTarget = new Map<string, UnresolvedLink>();
  for (const r of rows) {
    if (known.has(r.toKey) || !live.has(r.from)) continue;
    const entry = byTarget.get(r.toKey) ?? { rawTarget: r.rawTarget, toKey: r.toKey, from: [] };
    if (!entry.from.includes(r.from)) entry.from.push(r.from);
    byTarget.set(r.toKey, entry);
  }
  return [...byTarget.values()].sort(
    (a, b) => b.from.length - a.from.length || a.toKey.localeCompare(b.toKey),
  );
}

/** Live documents nothing links to, and which link nowhere themselves. */
export async function orphanDocuments(): Promise<MarkdownDocument[]> {
  const { docs, edges } = await linkGraph();
  const connected = new Set<string>();
  for (const e of edges) {
    connected.add(e.from);
    connected.add(e.to);
  }
  return docs.filter((d) => !connected.has(d.id)).sort((a, b) => b.updatedAt - a.updatedAt);
}

/** An edge between two live documents. */
export interface GraphEdge {
  from: string;
  to: string;
}

/** All resolved edges between live documents, for the graph view. */
export async function linkGraph(): Promise<{ docs: MarkdownDocument[]; edges: GraphEdge[] }> {
  const [rows, all] = await Promise.all([db.links.toArray(), db.documents.toArray()]);
  const docs = all.filter((d) => d.deletedAt == null);
  const byKey = new Map<string, string>();
  for (const d of docs) {
    for (const k of documentKeys(d)) byKey.set(k, d.id);
  }
  const ids = new Set(docs.map((d) => d.id));
  const seen = new Set<string>();
  const edges: GraphEdge[] = [];
  for (const r of rows) {
    if (!ids.has(r.from)) continue;
    const to = byKey.get(r.toKey);
    if (!to || to === r.from) continue;
    const dedupe = `${r.from} ${to}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    edges.push({ from: r.from, to });
  }
  return { docs, edges };
}

/** Resolve a link target to a live document id, matching titles then aliases. */
export async function resolveTarget(rawTarget: string): Promise<string | null> {
  const k = key(rawTarget);
  if (!k) return null;
  const live = (await db.documents.toArray()).filter((d) => d.deletedAt == null);
  return (
    live.find((d) => key(d.title || '') === k)?.id ??
    live.find((d) => (d.aliases ?? []).some((a) => key(a) === k))?.id ??
    null
  );
}
